"use client";

// Homepage social proof. TWO honest signals, no fabrication:
//
//  1. A live "audits run" counter fed by the real /api/stats endpoint
//     (completed, non-demo audits). It renders ONLY once the real number
//     clears MIN_AUDITS — below that, a small/zero number would undercut
//     more than it helps, so the strip hides itself. The displayed figure
//     is rounded DOWN to a clean step and suffixed "+", so it can only
//     ever understate the truth, never inflate it.
//
//  2. Real testimonials, sourced from the TESTIMONIALS array below. It is
//     intentionally EMPTY — drop genuine quotes in and the block appears.
//     Nothing placeholder or invented is ever rendered. If both the
//     counter and the quotes are absent, the whole section renders null.

import { useEffect, useState } from "react";
import { fetchPublicStats } from "../lib/api";

// Show the counter only once real usage clears this bar. Tune to taste.
const MIN_AUDITS = 250;
// Round the displayed number DOWN to this step (keeps it honest: "1,200+"
// means at least 1,200) and gives the figure an intentional, un-fiddly look.
const ROUND_STEP = 50;

interface Testimonial {
  quote: string;
  name: string;
  role: string;
}

// ⬇️  REAL QUOTES ONLY. The block stays hidden while this array is empty.
//     Quotes below are genuine, lightly trimmed to a faithful contiguous
//     excerpt. Attribution stays faithful: Paria from LinkedIn (real name +
//     title); claritybykat labelled "Founder" per her own "building my own
//     business" note; the third is anonymous. No invented names or titles.
const TESTIMONIALS: Testimonial[] = [
  {
    quote:
      "I used it on my own website to get some feedback and I loved it. I'm really impressed. Even though I worked with a designer to build that website a few years ago, Snap Site still picked up so many useful details that could make it much better.",
    name: "Paria Motiee",
    role: "Product & Digital Transformation Leader",
  },
  {
    quote:
      "It didn't just tell me there were problems somewhere on the site. It showed me exactly where they were and explained why they mattered. It caught things I had completely overlooked, like using different CTA labels for the same action and burying my pricing inside paragraphs.",
    name: "claritybykat",
    role: "Founder",
  },
  {
    quote:
      "Pinning issues to the actual spot on the page is way more useful than a generic checklist report.",
    name: "Anonymous",
    role: "Snap Site user",
  },
];

function formatCount(n: number): string {
  const rounded = Math.floor(n / ROUND_STEP) * ROUND_STEP;
  return `${rounded.toLocaleString()}+`;
}

export function SocialProof({ compact = false }: { compact?: boolean }) {
  const [audits, setAudits] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchPublicStats().then((stats) => {
      if (!cancelled && stats) setAudits(stats.audits);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const showCounter = audits != null && audits >= MIN_AUDITS;
  const showQuotes = TESTIMONIALS.length > 0;

  // Nothing real to show yet → render nothing (no empty shell, no fake bar).
  if (!showCounter && !showQuotes) return null;

  if (compact) {
    return (
      <section className="lp-proof-compact" aria-label="What a Snap Site customer found">
        <div>
          <blockquote>“It showed me exactly where they were and explained why they mattered.”</blockquote>
          <p><strong>claritybykat</strong> · Founder</p>
        </div>
        {showCounter ? <p className="lp-proof-compact-count"><strong>{formatCount(audits as number)}</strong> audits run on Snap Site</p> : null}
      </section>
    );
  }

  return (
    <section
      className="lp-section lp-full-bleed lp-proof"
      aria-labelledby="proof-heading"
    >
      <div className="lp-proof-inner">
        <div className="lp-section-head reveal">
          <p className="lp-kicker">From real users</p>
          <h2 id="proof-heading">What people say about Snap Site.</h2>
        </div>

        {showCounter ? (
          <div className="lp-proof-counter" role="status">
            <span className="lp-proof-count">{formatCount(audits as number)}</span>
            <span className="lp-proof-count-label">audits run on Snap Site</span>
          </div>
        ) : null}

        {showQuotes ? (
          <ul className="lp-proof-quotes">
            {TESTIMONIALS.map((t, i) => (
              <li key={i} className="lp-proof-quote">
                <span className="lp-proof-quote-pin" aria-hidden="true" />
                <span className="lp-proof-quote-mark" aria-hidden="true">
                  &ldquo;
                </span>
                <blockquote className="lp-proof-quote-text">{t.quote}</blockquote>
                <div className="lp-proof-quote-attr">
                  <span className="lp-proof-quote-avatar" aria-hidden="true">
                    {t.name.trim().charAt(0).toUpperCase()}
                  </span>
                  <span className="lp-proof-quote-who">
                    <span className="lp-proof-quote-name">{t.name}</span>
                    <span className="lp-proof-quote-role">{t.role}</span>
                  </span>
                </div>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </section>
  );
}
